/**
 * V3 liveSpeech SentenceItem envelope 构造（design D2 / saveSpeechContent 列路由）
 */

import { shortLangCode } from '../adapters/live-speech-adapter'
import { getLanguageName } from '@/shared/utils/language'

export interface SpeechTextCell {
  languageCode: string
  text: string
  readFlag: boolean
  languageName: string
}

export interface LiveSpeechEnvelopeData {
  duration: number
  beginTime?: number
  endTime?: number
  role: string
  speeker: string
  readText: SpeechTextCell
  listenText: SpeechTextCell[]
}

export interface LiveSpeechEnvelopeMessage {
  type: 'liveSpeech'
  data: LiveSpeechEnvelopeData
}

export interface BuildLiveSpeechEnvelopeInput {
  role: string
  speakerName: string
  /** SDK 识别原文 */
  voiceText: string
  /** PTT / 识别语种短码 */
  voiceLangShort: string
  /** SDK 译文，key 为 session 短码 */
  translations: Record<string, string>
  /** 当前 session 源语短码（lang/select 后以客户端最新值为准） */
  sessionSourceShort: string
  /** 当前 session 目标语短码列表（顺序与 session 一致） */
  sessionTargetShorts: string[]
  isFinal: boolean
  /** final 句时长（ms）；partial 忽略 */
  durationMs?: number
  /** final 结束时刻（ms）；缺省用 now */
  endTime?: number
  /** 可注入时钟，便于单测 */
  now?: number
}

/** final 时长：秒，至少 1 */
export function finalDurationSeconds(durationMs: number): number {
  return Math.max(1, Math.ceil(Math.max(0, durationMs) / 1000))
}

function dedupeSessionTargetShorts(targets: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const code of targets) {
    const short = shortLangCode(code)
    if (!short || seen.has(short)) {
      continue
    }
    seen.add(short)
    result.push(short)
  }
  return result
}

function cellTextForLanguage(
  langShort: string,
  voiceLangShort: string,
  voiceText: string,
  translations: Record<string, string>,
): string {
  if (langShort === voiceLangShort) {
    return voiceText.trim()
  }
  return (translations[langShort] ?? translations[shortLangCode(langShort)] ?? '').trim()
}

function shouldIncludeListenCell(text: string, voiceLangShort: string, targetShort: string): boolean {
  if (voiceLangShort === targetShort) {
    return true
  }
  return Boolean(text)
}

function buildSpeechTextCell(
  languageCode: string,
  text: string,
  readFlag: boolean,
): SpeechTextCell {
  return {
    languageCode,
    text,
    readFlag,
    languageName: getLanguageName(languageCode),
  }
}

/** 构造 uplink/downlink 同形 SentenceItem envelope */
export function buildLiveSpeechEnvelope(
  input: BuildLiveSpeechEnvelopeInput,
): LiveSpeechEnvelopeMessage {
  const voiceLangShort = shortLangCode(input.voiceLangShort)
  const sessionSourceShort = shortLangCode(input.sessionSourceShort)
  const sessionTargetShorts = dedupeSessionTargetShorts(input.sessionTargetShorts)
  const translations = Object.fromEntries(
    Object.entries(input.translations).map(([lang, text]) => [shortLangCode(lang), text]),
  )

  const readText = buildSpeechTextCell(
    sessionSourceShort,
    cellTextForLanguage(sessionSourceShort, voiceLangShort, input.voiceText, translations),
    voiceLangShort === sessionSourceShort,
  )

  const listenText: SpeechTextCell[] = []
  for (const targetShort of sessionTargetShorts) {
    const text = cellTextForLanguage(targetShort, voiceLangShort, input.voiceText, translations)
    if (!shouldIncludeListenCell(text, voiceLangShort, targetShort)) {
      continue
    }
    listenText.push(
      buildSpeechTextCell(targetShort, text, voiceLangShort === targetShort),
    )
  }

  const duration = input.isFinal
    ? finalDurationSeconds(input.durationMs ?? 0)
    : 0

  const data: LiveSpeechEnvelopeData = {
    duration,
    role: input.role,
    speeker: input.speakerName,
    readText,
    listenText,
  }

  if (input.isFinal && duration >= 1) {
    const endTime = input.endTime ?? input.now ?? Date.now()
    data.endTime = endTime
    data.beginTime = endTime - duration * 1000
  }

  return { type: 'liveSpeech', data }
}
